import { get } from '@ember/object';

import type { SortOrderDirection } from 'webapp-types';
import { generateWordsArray } from 'webapp/utils/common';
import { advancedSortFn, AdvancedSortProps } from 'webapp/utils/metric-sort';

import type { Metric } from 'webapp/models-custom';

export interface SortProps {
	prop: string;
	direction: SortOrderDirection;
}

/* Search that assigns points based on where the term was found
 * props example: `{ name: 5, key: 2, desc: 1 }`
 * defaultSort example: `{ prop: 'usedCount', direction: 'desc' }`
 * array items then should have keys `name`, `key`, `desc`, `id` (string)
 * returns new array of items (only those that matched, sorted by relevance)
 */

/* Searching and sorting works from multiple steps
 * A. Searching term is same as whole term - has the biggest priority (according to @totalMatchMultiplier.
 *	Example: prop modifier value will be 1 (also in point B) - prop modifier value is defined in specific filter (@searchProps)
 *		Term: Sessions by channel | Search Term: Sessions by Channel -> pts = 1 * 5 = 5;
 *			* @uTermsArray = ['SESSIONS', 'BY', 'CHANNEL']
 * B. We loop through array and for every item we calculate points to get priorities for sorting.
 *	 We loop also through props array - we can search by multiple props on item from @array.
 *	 Searching term is seperated into array with uniqe words - we loop through each word item in
 *	 @uTermsArray an we are assigning points to every item in @array.
 *	 While searching we check also if this is at the beggining of term or at the beggining of word in term. For this we have two multipliers - @startOfStringMultiplier and @startOfWordMultiplier.
 *	 When we have all this values we multiply props value with one of multipliers (@startOfStringMultiplier or @startOfWordMultiplier).
 *	 Items with more points are higher on list.
 *	 	Example:
 *			Term: Sessions by channel | Search Term: Sessions by Source -> pts = (1 * 1.4) + (1 * 1.2) = 2.6;
 *			* @uTermsArray = ['SESSIONS', 'BY', 'SOURCE']
 *			* 1.4 because SESSIONS is at the begginig of item prop value // val = get(item, prop);
 *			* 1.2 because BY is at the begginig of word in item prop value
 *			* SOURCE is not found in item prop value
 */

/**
 * prioritzeExactMatches and additionalSorts
 * at the end of the function we have additionalSorts array which holds functions that are used to sort the result array
 * if prioritizeExactMatches is true, then additionalSorts are applied only to non-exact matches
 */

// weight is the weight of the property in calculating the relevance of the item
// if exactMatch is true, the prop has to be an exact match for it to count (e.g. Search: "per" -> "percent" -> 0 points)
// minChars mean the minim search characters for the prop to be taken into account (default: 1)

interface SearchPropConfig {
	weight: number;
	exactMatch?: boolean;
	minChars?: number;
}

export type SearchProps = Record<string, number | SearchPropConfig>;

function searchAndSortByRelevance<T>(
	array: T[],
	props: SearchProps,
	defaultSort: SortProps | null,
	term: string,
	prioritizeExactMatches = true,
	additionalSorts: ((a: T, b: T) => number)[] = [],
): T[] {
	const resultArray: T[] = [],
		totalMatchMultiplier = 10,
		startOfStringMultiplier = 2,
		startOfWordMultiplier = 1.2,
		uTerm = term.toUpperCase(),
		uTermsArray = generateWordsArray(uTerm),
		itemData = new Map<T, { points: number; isExactMatch: boolean }>();

	array.forEach((item) => {
		let points = 0;
		let hasExactMatch = false;

		if (term) {
			Object.entries(props).forEach(([prop, config]) => {
				let pts = 0;
				const minChars =
					typeof config === 'object' && config.minChars ? config.minChars : 1;

				if (uTerm.length < minChars) {
					return;
				}

				const weight = typeof config === 'number' ? config : config.weight;
				const exactMatch = typeof config === 'object' && config.exactMatch;

				const val = get(item, prop),
					uVal = String(val).toUpperCase();

				if (uVal === uTerm) {
					pts = weight * totalMatchMultiplier;
					hasExactMatch = true;
				} else if (!exactMatch) {
					uTermsArray.forEach((item) => {
						const index = uVal.indexOf(item);
						let _pts = 0;
						if (index > -1) {
							_pts = weight;

							if (index === 0) {
								_pts *= startOfStringMultiplier;
							} else if (uVal[index - 1] === ' ') {
								_pts *= startOfWordMultiplier;
							}
						}

						pts += _pts;
					});
				}

				points += pts;
			});
		}

		if (!term || points > 0) {
			resultArray.push(item);
			itemData.set(item, { points, isExactMatch: hasExactMatch });
		}
	});

	const resultArrayCopy = resultArray.slice();
	resultArrayCopy.sort((a: T, b: T) => {
		const aData = itemData.get(a);
		const bData = itemData.get(b);
		const isAExactMatch = aData?.isExactMatch || false;
		const isBExactMatch = bData?.isExactMatch || false;

		// prioritize exact matches if the option is enabled
		if (prioritizeExactMatches && isAExactMatch !== isBExactMatch) {
			return isAExactMatch ? -1 : 1;
		}

		if (defaultSort) {
			if (defaultSort.prop === AdvancedSortProps.prop) {
				return advancedSortFn(a as Metric, b as Metric);
			}

			// order from defaultSort
			const propValueA = get(a, defaultSort.prop) || '',
				propValueB = get(b, defaultSort.prop) || '';

			if (propValueA === propValueB) {
				return 0;
			} else if (defaultSort.direction === 'desc') {
				return propValueA > propValueB ? -1 : 1;
			} else {
				return propValueA > propValueB ? 1 : -1;
			}
		} else {
			// order by relevance of weights
			const pointsForA = aData?.points || 0;
			const pointsForB = bData?.points || 0;

			// -1 : 'a' comes first
			// +1 : 'b' comes first
			//  0 : whatevers
			if (pointsForA > pointsForB) {
				return -1;
			} else if (pointsForB > pointsForA) {
				return 1;
			} else {
				return 0;
			}
		}
	});

	if (additionalSorts.length > 0) {
		resultArrayCopy.sort((a: T, b: T) => {
			const aData = itemData.get(a);
			const bData = itemData.get(b);
			const isAExactMatch = aData?.isExactMatch || false;
			const isBExactMatch = bData?.isExactMatch || false;

			// if either item is an exact match and prioritizeExactMatches is enabled,
			// preserve their relative order
			if (prioritizeExactMatches && (isAExactMatch || isBExactMatch)) {
				return 0;
			}

			// apply additional sorts only to non-exact matches
			for (const sortFn of additionalSorts) {
				const result = sortFn(a, b);
				if (result !== 0) {
					return result;
				}
			}
			return 0;
		});
	}

	return resultArrayCopy;
}

function matchSearchInProps(item: object, searchProps: string[], term: string): boolean {
	const termsArray = generateWordsArray(term.toLowerCase());

	if (!termsArray.length) {
		return true;
	}

	return searchProps.some((prop) => {
		const val = get(item, prop) as string | number | null | undefined; // the searched props are texts or numbers

		return termsArray.some((item) =>
			String(val || '')
				.toLowerCase()
				.includes(item),
		);
	});
}

function getCompactString(string: string): string {
	// good to save in url, etc (no whitespace)
	return string.replace(/\s|&/g, '');
}

export { searchAndSortByRelevance, matchSearchInProps, getCompactString };
